<template>
  <div class="pw-shell">
    <overlay :value="loading"></overlay>

    <v-app-bar color="primary" dark elevation="0" app>
      <v-app-bar-nav-icon @click.stop="toggleNav"></v-app-bar-nav-icon>
      <v-img src="@/assets/Isotipo_Negativo.png" max-width="40px" class="" contain></v-img>
      <v-toolbar-title class="d-none d-sm-block ml-4">Administrador <b>Pawer</b></v-toolbar-title>

      <v-spacer></v-spacer>

      <!-- Indicador de carga masiva en background -->
      <v-chip v-if="bulkTask && $route.path !== '/cargas-masivas'" small outlined color="white" class="mr-2">
        <v-progress-circular indeterminate size="14" width="2" color="white" class="mr-1" />
        {{ bulkTask.current }}/{{ bulkTask.total }} usuarios
      </v-chip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn @click="logout" icon v-bind="attrs" v-on="on">
            <v-icon>mdi-logout</v-icon>
          </v-btn>
        </template>
        <span class="text-center">Cerrar sesión</span>
      </v-tooltip>
    </v-app-bar>

    <!-- Snackbar de notificación de carga masiva -->
    <v-snackbar v-model="bulkSnackbar" :timeout="6000" :color="bulkSnackbarColor">
      {{ bulkSnackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="bulkSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <v-navigation-drawer
      v-model="drawer"
      :mini-variant="isDesktopNav && mini"
      app
      touchless
      :permanent="isDesktopNav"
      :temporary="!isDesktopNav"
      width="285"
      style="min-height: 90vh"
      fill-height
    >
      <v-list-item to="/profile" class="px-2 my-md-1" style="border: none">
        <v-list-item-avatar class="pw-avatar">
          <v-img v-if="user && user.imageUrl" :src="user.imageUrl"></v-img>
          <span v-else class="pw-avatar__initials">{{ userInitials }}</span>
        </v-list-item-avatar>
        <v-list-item-title>
          <p class="ma-0 font-weight-bold">{{ user ? user.name : '' }}</p>
          <p class="ma-0">{{ user ? user.email : '' }}</p>
        </v-list-item-title>
      </v-list-item>

      <v-divider></v-divider>
      <template>
        <v-list nav dense>
          <v-list-group
            no-action
            style="align-items: center;"
            v-for="(item, i) in items"
            :key="i"
            :prepend-icon="item.icon"
            :class="{ 'no-arrow': !(item.subLinks && item.subLinks.length) }"
            :value="item.open"
          >
            <template v-slot:activator>
              <v-list-item :to="item.link" style="align-items: center;">
                <v-list-item-title class="text-truncate">{{ item.title }}</v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item v-for="(sub, s) in item.subLinks" :key="s + 10000" :to="sub.link">
              <v-list-item-icon>
                <v-icon>{{ sub.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ sub.title }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
        </v-list>
      </template>
    </v-navigation-drawer>
    <v-main>
      <v-container fluid>
        <keep-alive include="CargasMasivas">
          <router-view />
        </keep-alive>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import Overlay from '../components/Overlay.vue';
import AuthService from '../services/auth.service';
import { getAllowedNavGroups } from '../utils/nav-sections.utils';
import { loadPermissions, resetPermissions } from '../utils/permissions';

export default {
  components: { Overlay },
  data() {
    return {
      loading: true,
      user: null,
      drawer: false,
      overwriteBreakpoint: false,
      bulkTask: null,
      bulkSnackbar: false,
      bulkSnackbarMessage: '',
      bulkSnackbarColor: '',
      // Filled once the admin's permissions are loaded (spec 131).
      items: []
    };
  },
  computed: {
    // Iniciales en vez del retrato genérico que se cargaba desde Wikimedia.
    userInitials() {
      const partes = String((this.user && this.user.name) || '').trim().split(/\s+/).filter(Boolean);
      if (!partes.length) return '';
      return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
    },
    mini: {
      get() {
        return this.overwriteBreakpoint;
      },
      set(value) {
        this.overwriteBreakpoint = value;
      }
    },
    isDesktopNav() {
      return this.$vuetify.breakpoint.mdAndUp;
    }
  },
  watch: {
    $route() {
      if (!this.isDesktopNav) this.drawer = false;
    }
  },
  mounted() {
    this.getUserData();
    this.$eventBus.$on('bulk-start', ({ total }) => {
      this.bulkTask = { current: 0, total };
    });
    this.$eventBus.$on('bulk-progress', ({ current, total }) => {
      this.bulkTask = { current, total };
    });
    this.$eventBus.$on('bulk-complete', ({ message, isError }) => {
      this.bulkTask = null;
      if (this.$route.path !== '/cargas-masivas') {
        this.bulkSnackbarMessage = message;
        this.bulkSnackbarColor = isError ? 'red' : 'green';
        this.bulkSnackbar = true;
      }
    });
  },

  beforeDestroy() {
    this.$eventBus.$off('bulk-start');
    this.$eventBus.$off('bulk-progress');
    this.$eventBus.$off('bulk-complete');
  },
  methods: {
    toggleNav() {
      if (this.isDesktopNav) {
        this.mini = !this.mini;
      } else {
        this.drawer = !this.drawer;
      }
    },
    async getUserData() {
      // Same cached /me the router guard already loaded: one request, and the
      // drawer only renders the sections this admin may open.
      const res = await loadPermissions();
      if (res.status === 'ok') {
        this.user = res.data;
        this.items = getAllowedNavGroups();
      } else {
        // A session refused for a missing second factor is sent to the step it
        // needs, not to a generic login bounce.
        this.$router.push({ path: res.redirectTo || '/login' });
      }
      this.loading = false;
    },
    async logout() {
      this.loading = true;
      resetPermissions();
      const res = await AuthService.logout();
      if (res.status === 'ok') {
        this.$router.push({ path: '/login' });
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.pw-shell {
  min-height: 100vh;
}

.pw-avatar {
  background-color: var(--pw-primary-tint);
  justify-content: center;
}
.pw-avatar__initials {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  color: var(--pw-primary);
}
</style>
